import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import { useAuth } from '../lib/AuthContext';
import './Dashboard.css';
import ShareButton from '../components/ShareButton';
import SettingsPanel from '../components/SettingsPanel';
import ArticleContentWrapper from '../components/ArticleContentWrapper';
import { buildPreviewHTML } from '../components/ArticleEditor';
import orangeLogo from '../assets/orangelog.png';

/* ── Design tokens (matches LP5) ── */
const T = {
  accent:  '#dd3333',
  header:  '#111111',
  border:  '#e8e8e8',
  bodyText:'#444',
  headText:'#111',
  meta:    '#777',
  lightBg: '#f4f4f4',
  white:   '#fff',
};

const GENRE_COLOR = {
  'National News': '#dd3333',
  'State News':    '#4db2ec',
  'Local News':    '#27ae60',
  'Sports':        '#e67e22',
  'Technology':    '#8e44ad',
};

function fmtDate(d) {
  return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

/* Right-sidebar article card — LP5 SideCard style */
function SideCard({ article, onClick }) {
  const color = GENRE_COLOR[article.genre_tree?.main] || T.accent;
  const [hov, setHov] = useState(false);
  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{ display: 'flex', gap: '8px', cursor: 'pointer', paddingBottom: '10px', borderBottom: `1px solid ${T.border}`, marginBottom: '10px' }}
    >
      {article.headline_image && (
        <div style={{ width: '60px', height: '45px', flexShrink: 0, overflow: 'hidden' }}>
          <img src={article.headline_image} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        </div>
      )}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: '10px', fontWeight: '800', color, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '2px', fontFamily: 'Arial, sans-serif' }}>
          {article.genre_tree?.main}
        </div>
        <div style={{ fontSize: '13px', fontWeight: '700', color: hov ? color : T.headText, lineHeight: '18px', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', transition: 'color 0.12s', fontFamily: 'Georgia, serif' }}>
          {article.title}
        </div>
        <div style={{ fontSize: '11px', color: T.meta, marginTop: '2px', fontFamily: 'Arial, sans-serif' }}>
          {fmtDate(article.date_created)}
        </div>
      </div>
    </div>
  );
}

function stripHtml(html) {
  const tmp = document.createElement('div');
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || '';
}

function getSessionId() {
  let sid = sessionStorage.getItem('pubsilo_sid');
  if (!sid) {
    sid = Math.random().toString(36).slice(2) + Date.now().toString(36);
    sessionStorage.setItem('pubsilo_sid', sid);
  }
  return sid;
}

function CompanyPage() {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const pinnedArticleId = searchParams.get('a');
  const navigate = useNavigate();

  /* ── Existing state ── */
  const [company, setCompany]                   = useState(null);
  const [articles, setArticles]                 = useState([]);
  const [user, setUser]                         = useState(null);
  const [selectedArticle, setSelectedArticle]   = useState(null);
  const [loading, setLoading]                   = useState(true);
  const [error, setError]                       = useState('');
  const [confirmModal, setConfirmModal]         = useState(null);
  const [activeGenre, setActiveGenre]           = useState(null);
  const [activeSub, setActiveSub]               = useState(null);
  const [kudosGiven, setKudosGiven]             = useState({});
  const [kudosCounts, setKudosCounts]           = useState({});
  const [following, setFollowing]               = useState([]);
  const [followingAuthors, setFollowingAuthors] = useState([]);
  const [articleScrolled, setArticleScrolled]   = useState(false);
  const [featuredContent, setFeaturedContent]   = useState(null);
  const [readingArticle, setReadingArticle]      = useState(false);
  const [navScrolled, setNavScrolled]            = useState(false);

  /* ── New state for LP5-style navbar token dropdown ── */
  const [tokenDropOpen, setTokenDropOpen] = useState(false);
  const tokenDropRef = useRef(null);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);
  const [mobileGenreOpen, setMobileGenreOpen] = useState(false);

  const companyHeartbeatRef  = useRef(null);
  const articleHeartbeatRef  = useRef(null);
  const openReadingOnLoadRef = useRef(false);

  const { accessToken: token, clearSession } = useAuth();
  const userType    = localStorage.getItem('userType');
  const username    = localStorage.getItem('username');
  const companySlug = localStorage.getItem('companyName');
  const isAdmin     = localStorage.getItem('isAdmin') === 'true';
  const isCompanyUser = userType === 'company';
  const isOwnCompanyPage = isCompanyUser &&
    decodeURIComponent(slug).toLowerCase() === (companySlug || '').toLowerCase();

  /* ── Effects (unchanged) ── */
  useEffect(() => { fetchCompanyData(); }, [slug]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    setArticleScrolled(false);
    if (openReadingOnLoadRef.current) {
      openReadingOnLoadRef.current = false;
      setReadingArticle(true);
    } else {
      setReadingArticle(false);
    }
  }, [selectedArticle]);

  useEffect(() => {
    if (!company) return;
    const sid = getSessionId();
    const send = () => axios.post('/api/analytics/heartbeat', { type: 'company', id: company._id, sessionId: sid }).catch(() => {});
    send();
    companyHeartbeatRef.current = setInterval(send, 30000);
    return () => clearInterval(companyHeartbeatRef.current);
  }, [company]);

  useEffect(() => {
    clearInterval(articleHeartbeatRef.current);
    if (!selectedArticle) return;
    const sid = getSessionId();
    const send = () => axios.post('/api/analytics/heartbeat', { type: 'article', id: selectedArticle._id, sessionId: sid }).catch(() => {});
    send();
    articleHeartbeatRef.current = setInterval(send, 30000);
    return () => clearInterval(articleHeartbeatRef.current);
  }, [selectedArticle]);

  useEffect(() => {
    if (selectedArticle) window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [selectedArticle]);

  /* Window scroll — navbar shrink while reading + sticky article header */
  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY;
      setNavScrolled(y > 50);
      setArticleScrolled(readingArticle ? y > 200 : false);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [readingArticle]);

  /* Scroll to top when entering reading mode */
  useEffect(() => {
    if (readingArticle) window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [readingArticle]);

  /* Close token dropdown on outside click */
  useEffect(() => {
    if (!tokenDropOpen) return;
    const handler = (e) => {
      if (tokenDropRef.current && !tokenDropRef.current.contains(e.target)) setTokenDropOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [tokenDropOpen]);

  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);


  /* ── Data fetch (unchanged) ── */
  const fetchCompanyData = async () => {
    try {
      setLoading(true);
      setError('');
      const companyResponse = await axios.get(`/api/companies/public/${slug}`);
      const foundCompany = companyResponse.data;
      setCompany(foundCompany);

      const articlesResponse = await axios.get(`/api/articles?company_id=${foundCompany._id}`);
      const companyArticles = articlesResponse.data;
      setArticles(companyArticles);

      if (companyArticles.length > 0) {
        if (pinnedArticleId) {
          const matched = companyArticles.find(a => a._id.toString() === pinnedArticleId);
          if (matched && searchParams.get('read') === '1') {
            openReadingOnLoadRef.current = true;
            navigate(`${window.location.pathname}?a=${pinnedArticleId}`, { replace: true });
          }
          setSelectedArticle(matched || companyArticles[0]);
        } else {
          setSelectedArticle(companyArticles[0]);
        }
      }

      if (token && !isCompanyUser) {
        try {
          const headers = { Authorization: `Bearer ${token}` };
          const [userResponse, followRes] = await Promise.all([
            axios.get('/api/users/me', { headers }),
            axios.get('/api/users/me/following', { headers }),
          ]);
          setUser(userResponse.data);
          setFollowing((followRes.data.companies || []).map(c => c._id));
          setFollowingAuthors((followRes.data.authors || []).map(a => a._id));
        } catch {}
      }
    } catch (err) {
      if (err.response?.status === 404) setError('Company not found.');
      else setError('Failed to load company page. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  /* ── Handlers (unchanged) ── */
  const handleLogout = () => {
    if (token) axios.post('/api/auth/logout', {}, { headers: { Authorization: `Bearer ${token}` } }).catch(() => {});
    clearSession();
    navigate('/');
  };

  const isArticlePurchased = (articleId) =>
    user?.purchased?.some(item => item.articleId?.toString() === articleId?.toString() && !item.returned);

  const getPurchaseState = (article) => {
    if (!user) return 'login';
    const cost = article.token_cost;
    const bal  = user.tokens;
    if (bal >= cost) return 'normal';
    if (bal > 0 && cost - bal <= 15 && (user.promoTokens || 0) === 0) return 'promo';
    return 'insufficient';
  };

  const openConfirm = (article) => {
    if (!token) {
      navigate(`/login?next=${encodeURIComponent(window.location.pathname + window.location.search)}`);
      return;
    }
    const state = getPurchaseState(article);
    if (state === 'insufficient') return;
    setConfirmModal({ article, state });
  };

  const confirmPurchase = async () => {
    const { article } = confirmModal;
    setConfirmModal(null);
    try {
      const response = await axios.post(
        `/api/users/me/purchase/${article._id}`, {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setUser(prev => ({ ...prev, ...response.data.user }));
      setError('');
      if (article.external_url) {
        window.open(article.external_url, '_blank', 'noopener,noreferrer');
      } else {
        setSelectedArticle(article);
        setReadingArticle(true);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Purchase failed');
    }
  };

  const isFollowingCompany = company && following.includes(company._id);

  const handleFollowCompany = async () => {
    if (!token || isCompanyUser) return;
    const headers = { Authorization: `Bearer ${token}` };
    try {
      if (isFollowingCompany) {
        await axios.delete(`/api/users/me/follow/company/${company._id}`, { headers });
        setFollowing(prev => prev.filter(id => id !== company._id));
      } else {
        await axios.post(`/api/users/me/follow/company/${company._id}`, {}, { headers });
        setFollowing(prev => [...prev, company._id]);
      }
    } catch {}
  };

  const handleFollowAuthor = async (authorId) => {
    if (!token || isCompanyUser) return;
    const headers = { Authorization: `Bearer ${token}` };
    const already = followingAuthors.includes(authorId);
    try {
      if (already) {
        await axios.delete(`/api/users/me/follow/author/${authorId}`, { headers });
        setFollowingAuthors(prev => prev.filter(id => id !== authorId));
      } else {
        await axios.post(`/api/users/me/follow/author/${authorId}`, {}, { headers });
        setFollowingAuthors(prev => [...prev, authorId]);
      }
    } catch {}
  };

  /* ── Computed values ── */
  const siloHref = (() => {
    if (!token) return null;
    if (isAdmin)                return '/admin';
    if (userType === 'company') return `/adminHUD/${encodeURIComponent(companySlug || '')}`;
    if (userType === 'author')  return `/${encodeURIComponent(companySlug || '')}/author/${encodeURIComponent(username || '')}`;
    if (userType === 'editor')  return `/${encodeURIComponent(companySlug || '')}/editor/${encodeURIComponent(username || '')}`;
    if (userType === 'user')    return `/${username}`;
    return null;
  })();

  const genreMap = {};
  articles.forEach(a => {
    const g = a.genre_tree?.main;
    if (!g) return;
    if (!genreMap[g]) genreMap[g] = new Set();
    if (a.genre_tree?.sub) genreMap[g].add(a.genre_tree.sub);
  });
  const genreList = Object.keys(genreMap);
  const subs = activeGenre ? [...genreMap[activeGenre]].filter(s => s && s !== 'Other') : [];

  // Articles visible to this visitor: sunset articles hidden unless already purchased
  const now = new Date();
  const visibleArticles = articles.filter(a => {
    const isSunset = a.sunsetDate && new Date(a.sunsetDate) <= now;
    if (isSunset && !isArticlePurchased(a._id)) return false;
    return true;
  });

  const featured = (() => {
    if (activeGenre) {
      return visibleArticles.find(a =>
        a.genre_tree?.main === activeGenre &&
        (!activeSub || a.genre_tree?.sub === activeSub || a.genre_tree?.sub?.startsWith(activeSub))
      ) || null;
    }
    // If the selected article is sunset and this user hasn't purchased it, show the first visible one instead
    if (selectedArticle) {
      const isSunset = selectedArticle.sunsetDate && new Date(selectedArticle.sunsetDate) <= now;
      if (isSunset && !isArticlePurchased(selectedArticle._id)) {
        return visibleArticles[0] || null;
      }
    }
    return selectedArticle;
  })();

  // Placed after `featured` so the dependency array can reference it without a TDZ error.
  // eslint-disable-next-line react-hooks/rules-of-hooks
  useEffect(() => {
    const id = featured?._id;
    if (!id) { setFeaturedContent(null); return; }
    const canRead = isOwnCompanyPage || isArticlePurchased(id);
    if (!canRead) { setFeaturedContent(null); return; }
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    axios.get(`/api/articles/${id}`, { headers })
      .then(res => setFeaturedContent(res.data.content ? buildPreviewHTML(res.data.content) : null))
      .catch(() => setFeaturedContent(null));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [featured?._id, isOwnCompanyPage, token, user?.purchased]);

  const featuredId = featured?._id?.toString();
  const filteredMore = visibleArticles.filter(a => {
    if (a._id.toString() === featuredId) return false;
    if (activeGenre && a.genre_tree?.main !== activeGenre) return false;
    if (activeSub && a.genre_tree?.sub !== activeSub && !a.genre_tree?.sub?.startsWith(activeSub)) return false;
    return true;
  });

  /* ── Kudos helpers (used in both feed and reading mode) ── */
  const artKudos = featured ? (kudosCounts[featured._id] || { content: featured.kudos || 0, author: 0 }) : { content: 0, author: 0 };
  const artGiven = featured ? (kudosGiven[featured._id] || { content: false, author: false }) : { content: false, author: false };
  const toggleKudos = async (type) => {
    if (!featured) return;
    const already = artGiven[type];
    try {
      if (type === 'content') {
        const res = already
          ? await axios.delete(`/api/articles/${featured._id}/kudos`)
          : await axios.post(`/api/articles/${featured._id}/kudos`);
        setKudosCounts(prev => ({ ...prev, [featured._id]: { ...artKudos, content: res.data.kudos } }));
      } else if (type === 'author' && featured.writer_id) {
        const res = already
          ? await axios.delete(`/api/authors/${featured.writer_id}/kudos`)
          : await axios.post(`/api/authors/${featured.writer_id}/kudos`);
        setKudosCounts(prev => ({ ...prev, [featured._id]: { ...artKudos, author: res.data.kudos } }));
      }
      setKudosGiven(prev => ({ ...prev, [featured._id]: { ...artGiven, [type]: !already } }));
    } catch {}
  };

  /* ── Genre chip style helper ── */
  const chipStyle = (active, genre) => ({
    fontSize: '10px', fontWeight: '800', textTransform: 'uppercase', letterSpacing: '0.07em',
    padding: '3px 10px', borderRadius: '3px', cursor: 'pointer', fontFamily: 'Arial, sans-serif',
    background: active ? (GENRE_COLOR[genre] || T.accent) : '#333',
    color: T.white,
    transition: 'background 0.12s',
    userSelect: 'none',
  });

  /* ── Navbar height (shrinks 50px while reading + scrolled) ── */
  const navRowHeight   = (navScrolled && readingArticle) ? 60 : 110;
  const navTotalHeight = navRowHeight + 36;

  /* ── Loading / error states ── */
  const LoadingNavbar = () => (
    <nav style={{ background: T.header, display: 'grid', gridTemplateColumns: '220px 1fr 300px', gridTemplateRows: '110px 36px', position: 'sticky', top: 0, zIndex: 1000, boxShadow: '0 2px 12px rgba(0,0,0,0.6)' }}>
      <div style={{ gridColumn: 1, gridRow: '1 / 3', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#F41238' }}>
        <img src={orangeLogo} alt="PubSilo" style={{ height: '90%', maxWidth: '210px', objectFit: 'contain', cursor: 'pointer' }} onClick={() => navigate('/')} />
      </div>
      <div style={{ gridColumn: 2, gridRow: 1, background: '#13273F' }} />
      <div style={{ gridColumn: 2, gridRow: 2, background: '#13273F' }} />
      <div style={{ gridColumn: 3, gridRow: '1 / 3', background: '#13273F' }} />
    </nav>
  );

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: T.white }}>
        <LoadingNavbar />
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 'calc(100vh - 146px)' }}>
          <p style={{ color: T.meta, fontFamily: 'Arial, sans-serif' }}>Loading…</p>
        </div>
      </div>
    );
  }

  if (!company) {
    return (
      <div style={{ minHeight: '100vh', background: T.white }}>
        <LoadingNavbar />
        <div style={{ textAlign: 'center', padding: '4rem 2rem' }}>
          <h2 style={{ color: T.accent, fontFamily: 'Georgia, serif' }}>{error || 'Company not found'}</h2>
          <button onClick={() => navigate('/')} style={{ marginTop: '1.5rem', padding: '0.6rem 1.5rem', background: T.accent, color: T.white, border: 'none', borderRadius: '6px', fontWeight: '700', cursor: 'pointer', fontFamily: 'Arial, sans-serif' }}>
            Back to Home
          </button>
        </div>
      </div>
    );
  }

  /* ── Main render ── */
  return (
    <>
      <Helmet>
        <title>{company.company_name} — PubSilo</title>
        <meta name="description" content={`Read articles from ${company.company_name} on PubSilo — The Content Marketplace.`} />
        {company.hero_image && <meta property="og:image" content={company.hero_image} />}
        {company.hero_image && <meta name="twitter:card" content="summary_large_image" />}
        {company.hero_image && <meta name="twitter:image" content={company.hero_image} />}
      </Helmet>
      <div style={{ minHeight: '100vh', background: T.white, fontFamily: "'Georgia', serif", color: T.bodyText }}>

        {/* ── Navbar ── */}
        {isMobile ? (
          <nav style={{ position: 'sticky', top: 0, zIndex: 1000, boxShadow: '0 2px 8px rgba(0,0,0,0.4)' }}>
            {/* Row 1: 70px — 1/3 PubSilo logo | 2/3 company name/logo */}
            <div style={{ display: 'flex', height: '70px' }}>
              <div style={{ width: '33.33%', background: '#F41238', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, cursor: 'pointer' }} onClick={() => navigate('/')}>
                <img src={orangeLogo} alt="PubSilo" style={{ height: '80%', objectFit: 'contain' }} />
              </div>
              <div style={{ width: '66.67%', background: company.navbar_color || '#13273F', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 10px', overflow: 'hidden' }}>
                {company.hero_image ? (
                  <img src={company.hero_image} alt={company.company_name} style={{ maxHeight: '55px', maxWidth: '100%', objectFit: 'contain' }} />
                ) : (
                  <div style={{ color: T.white, fontSize: '16px', fontWeight: '900', fontFamily: 'Arial, sans-serif', textAlign: 'center', lineHeight: '1.2' }}>{company.company_name}</div>
                )}
              </div>
            </div>

            {/* Row 2: 40px — token info + My Silo / Login + Sign Up */}
            <div style={{ background: company.navbar_color || '#13273F', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 12px', height: '40px', borderTop: '1px solid rgba(255,255,255,0.08)', gap: '8px' }}>
              {token ? (
                <>
                  {userType === 'user' && user && (
                    <div
                      onClick={() => navigate(`/tokens/purchase?next=${encodeURIComponent(`/${slug}`)}`)}
                      style={{ display: 'flex', alignItems: 'center', gap: '5px', cursor: 'pointer', background: '#1e1e1e', border: '1px solid #333', borderRadius: '5px', padding: '5px 10px', color: T.white, fontSize: '12px', fontFamily: 'Arial, sans-serif', flexShrink: 0 }}
                    >
                      <span>🪙</span>
                      <span style={{ fontWeight: '700' }}>{user.tokens}</span>
                      <span style={{ fontSize: '10px', color: T.accent, marginLeft: '2px', fontWeight: '700' }}>+ Buy</span>
                    </div>
                  )}
                  {siloHref && (
                    <Link to={siloHref} style={{ background: T.accent, color: T.white, padding: '6px 14px', borderRadius: '4px', textDecoration: 'none', fontSize: '12px', fontWeight: '700', fontFamily: 'Arial, sans-serif', whiteSpace: 'nowrap', marginLeft: 'auto', flexShrink: 0 }}>
                      {userType === 'company' ? 'Admin Page' : 'My Silo'}
                    </Link>
                  )}
                </>
              ) : (
                <>
                  <Link to="/login" style={{ color: '#ccc', textDecoration: 'none', fontSize: '12px', fontFamily: 'Arial, sans-serif', fontWeight: '700', padding: '5px 12px', border: '1px solid #3a3a3a', borderRadius: '4px', flexShrink: 0 }}>Login</Link>
                  <Link to="/signup/role" style={{ background: T.accent, color: T.white, textDecoration: 'none', fontSize: '12px', fontFamily: 'Arial, sans-serif', fontWeight: '700', padding: '6px 14px', borderRadius: '4px', whiteSpace: 'nowrap', flexShrink: 0 }}>Get Started</Link>
                </>
              )}
            </div>

            {/* Row 3: full-width Filter button with dropdown */}
            {genreList.length > 0 && (
              <div style={{ position: 'relative' }}>
                <button
                  onClick={() => setMobileGenreOpen(o => !o)}
                  style={{ width: '100%', background: '#0a1828', color: activeGenre ? T.accent : '#aaa', border: 'none', borderTop: '1px solid #1a2f45', height: '36px', fontSize: '12px', fontWeight: '700', fontFamily: 'Arial, sans-serif', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', letterSpacing: '0.05em', textTransform: 'uppercase' }}
                >
                  <span>{activeGenre ? `Filter: ${activeGenre}` : 'Filter'}</span>
                  <span style={{ fontSize: '9px' }}>{mobileGenreOpen ? '▲' : '▼'}</span>
                </button>
                {mobileGenreOpen && (
                  <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: '#0f1e2e', zIndex: 100, borderBottom: '2px solid #1a2f45', boxShadow: '0 4px 12px rgba(0,0,0,0.5)' }}>
                    {['', ...genreList].map(g => (
                      <div
                        key={g || 'all'}
                        onClick={() => { setActiveGenre(g || null); setActiveSub(null); setMobileGenreOpen(false); }}
                        style={{ padding: '11px 16px', fontSize: '13px', fontFamily: 'Arial, sans-serif', cursor: 'pointer', color: activeGenre === (g || null) ? T.accent : T.white, fontWeight: activeGenre === (g || null) ? '700' : '400', background: activeGenre === (g || null) ? 'rgba(221,51,51,0.1)' : 'transparent', borderBottom: '1px solid #1a2f45' }}
                      >
                        {g || 'All Genres'}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </nav>
        ) : (
          <nav style={{
            background: T.header,
            display: 'grid',
            gridTemplateColumns: '220px 1fr 300px',
            gridTemplateRows: `${navRowHeight}px 36px`,
            transition: 'grid-template-rows 0.3s ease',
            position: 'sticky', top: 0, zIndex: 1000,
            boxShadow: '0 2px 12px rgba(0,0,0,0.6)',
          }}>
            {/* Logo — spans both rows */}
            <div style={{ gridColumn: 1, gridRow: '1 / 3', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#F41238' }}>
              <img
                src={orangeLogo} alt="PubSilo"
                style={{ height: `${navRowHeight * 0.9}px`, maxWidth: '210px', objectFit: 'contain', cursor: 'pointer', transition: 'height 0.3s ease' }}
                onClick={() => navigate('/')}
              />
            </div>

            {/* Center row 1: company logo / name */}
            <div style={{ gridColumn: 2, gridRow: 1, background: company.navbar_color || '#13273F', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {company.hero_image ? (
                <img
                  src={company.hero_image}
                  alt={company.company_name}
                  style={{ maxHeight: `${navRowHeight - 20}px`, maxWidth: '100%', objectFit: 'contain', transition: 'max-height 0.3s ease' }}
                />
              ) : (
                <div style={{ color: T.white, fontSize: '22px', fontWeight: '900', fontFamily: 'Arial, sans-serif', letterSpacing: '0.03em' }}>
                  {company.company_name}
                </div>
              )}
            </div>

            {/* Center row 2: genre filter chips */}
            <div style={{ gridColumn: 2, gridRow: 2, background: company.navbar_color || '#13273F', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', flexWrap: 'wrap' }}>
              {genreList.length > 0 && (
                <>
                  <span onClick={() => { setActiveGenre(null); setActiveSub(null); }} style={chipStyle(activeGenre === null, null)}>
                    All
                  </span>
                  {genreList.map(g => (
                    <span
                      key={g}
                      onClick={() => { setActiveGenre(activeGenre === g ? null : g); setActiveSub(null); }}
                      style={chipStyle(activeGenre === g, g)}
                    >
                      {g}
                    </span>
                  ))}
                </>
              )}
            </div>

            {/* Right: auth controls — spans both rows */}
            <div style={{ gridColumn: 3, gridRow: '1 / 3', background: company.navbar_color || '#13273F', display: 'flex', justifyContent: 'center', gap: '10px', alignItems: 'center' }}>
              {token ? (
                <>
                  {userType === 'user' && user && (
                    <div ref={tokenDropRef} style={{ position: 'relative' }}>
                      <div
                        onClick={() => setTokenDropOpen(o => !o)}
                        style={{ display: 'flex', alignItems: 'center', gap: '5px', cursor: 'pointer', background: '#1e1e1e', border: '1px solid #333', borderRadius: '6px', padding: '7px 13px', color: T.white, fontSize: '13px', fontFamily: 'Arial, sans-serif', userSelect: 'none' }}
                      >
                        <span>🪙</span>
                        <span style={{ fontWeight: '700' }}>{user.tokens}</span>
                        <span style={{ fontSize: '9px', color: '#777', marginLeft: '2px' }}>▼</span>
                      </div>
                      {tokenDropOpen && (
                        <div style={{ position: 'absolute', top: 'calc(100% + 6px)', right: 0, background: '#1a1a1a', border: '1px solid #2e2e2e', borderRadius: '8px', padding: '10px 0', minWidth: '160px', zIndex: 1001, boxShadow: '0 6px 20px rgba(0,0,0,0.5)' }}>
                          <div style={{ padding: '4px 14px 8px', borderBottom: '1px solid #2a2a2a', marginBottom: '4px' }}>
                            <div style={{ fontSize: '11px', color: '#555', textTransform: 'uppercase', letterSpacing: '0.07em', fontFamily: 'Arial, sans-serif' }}>Balance</div>
                            <div style={{ fontSize: '20px', fontWeight: '700', color: T.white, fontFamily: 'Arial, sans-serif' }}>{user.tokens} tokens</div>
                          </div>
                          <div
                            onClick={() => { setTokenDropOpen(false); navigate(`/tokens/purchase?next=${encodeURIComponent(`/${slug}`)}`); }}
                            style={{ padding: '8px 14px', fontSize: '13px', color: T.accent, cursor: 'pointer', fontFamily: 'Arial, sans-serif', fontWeight: '700' }}
                            onMouseEnter={e => e.currentTarget.style.background = '#262626'}
                            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                          >
                            + Buy More Tokens
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                  {siloHref && (
                    <Link to={siloHref} style={{ background: T.accent, color: T.white, padding: '9px 18px', borderRadius: '5px', textDecoration: 'none', fontSize: '13px', fontWeight: '700', fontFamily: 'Arial, sans-serif', letterSpacing: '0.02em', whiteSpace: 'nowrap' }}>
                      {userType === 'company' ? 'Admin Page' : 'My Silo'}
                    </Link>
                  )}
                </>
              ) : (
                <>
                  <Link to="/login"
                    style={{ color: '#ccc', textDecoration: 'none', fontSize: '13px', fontFamily: 'Arial, sans-serif', fontWeight: '700', padding: '8px 15px', border: '1px solid #3a3a3a', borderRadius: '5px' }}
                    onMouseEnter={e => { e.currentTarget.style.background = '#222'; e.currentTarget.style.color = T.white; }}
                    onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#ccc'; }}
                  >
                    Login
                  </Link>
                  <Link to="/signup/role" style={{ background: T.accent, color: T.white, textDecoration: 'none', fontSize: '13px', fontFamily: 'Arial, sans-serif', fontWeight: '700', padding: '9px 18px', borderRadius: '5px', whiteSpace: 'nowrap' }}>
                    Get Started
                  </Link>
                </>
              )}
            </div>
          </nav>
        )}

        {/* ── Body ── */}
        <div style={{ display: 'flex', alignItems: 'flex-start' }}>

          {/* Left sidebar — desktop only */}
          {!isMobile && <div style={{ width: '220px', flexShrink: 0, borderRight: `1px solid ${T.border}`, position: 'sticky', top: `${navTotalHeight}px`, height: `calc(100vh - ${navTotalHeight}px)`, overflowY: 'auto', display: 'flex', flexDirection: 'column', padding: '16px 0', transition: 'top 0.3s ease, height 0.3s ease' }}>

            {/* Company card */}
            <div style={{ padding: '0 10px', marginBottom: '14px' }}>
              <div style={{ background: T.lightBg, borderRadius: '6px', padding: '14px 12px', border: `1px solid ${T.border}` }}>
                <div style={{ fontSize: '13px', fontWeight: '800', color: T.headText, fontFamily: 'Arial, sans-serif', marginBottom: '4px', lineHeight: '18px' }}>
                  {company.company_name}
                </div>
                {company.description && (
                  <div style={{ fontSize: '11px', color: T.meta, fontFamily: 'Arial, sans-serif', lineHeight: '16px', marginBottom: '8px', display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                    {company.description}
                  </div>
                )}
                <div style={{ fontSize: '11px', color: T.meta, fontFamily: 'Arial, sans-serif' }}>
                  {articles.length} article{articles.length !== 1 ? 's' : ''}
                </div>
              </div>
            </div>

            {/* Follow button */}
            {token && !isCompanyUser && (
              <div style={{ padding: '0 10px', marginBottom: '20px' }}>
                <button
                  onClick={handleFollowCompany}
                  style={{
                    width: '200px', boxSizing: 'border-box', padding: '9px', border: 'none', borderRadius: '4px', cursor: 'pointer',
                    background: isFollowingCompany ? '#f0f0f0' : T.accent,
                    color: isFollowingCompany ? T.headText : T.white,
                    fontSize: '11px', fontWeight: '900', textTransform: 'uppercase', letterSpacing: '0.07em', fontFamily: 'Arial, sans-serif',
                    transition: 'background 0.15s, color 0.15s',
                  }}
                >
                  {isFollowingCompany ? '✓ Following' : '+ Follow'}
                </button>
              </div>
            )}

            {/* Spacer — pushes settings to bottom */}
            <div style={{ flex: 1 }} />

            {/* Settings panel pinned to bottom */}
            {token && <SettingsPanel onLogout={handleLogout} />}
          </div>}

          {/* Center content */}
          <div className="main-content" style={{ flex: 1, minWidth: 0, padding: isMobile ? '12px' : '20px 20px 48px' }}>

            {error && <div style={{ color: T.accent, marginBottom: '1rem', fontFamily: 'Arial, sans-serif' }}>{error}</div>}

            {/* Subcategory chips (shown when a genre is active and has subs) */}
            {subs.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem', marginBottom: '1.25rem' }}>
                {subs.map(s => (
                  <button
                    key={s}
                    onClick={() => setActiveSub(activeSub === s ? null : s)}
                    style={{
                      padding: '0.25rem 0.75rem', borderRadius: '20px', fontSize: '0.76rem', fontWeight: '500',
                      border: `1px solid ${activeSub === s ? '#555' : '#ddd'}`, cursor: 'pointer',
                      backgroundColor: activeSub === s ? '#555' : '#f4f4f4',
                      color: activeSub === s ? '#fff' : '#555',
                    }}
                  >
                    {s}
                  </button>
                ))}
              </div>
            )}

            {/* ── READING MODE: article replaces feed ── */}
            {readingArticle && featured ? (
              <div style={{ maxWidth: '820px' }}>

                {/* Navigation bar: back + close */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem' }}>
                  <button
                    onClick={() => { setReadingArticle(false); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '0.4rem 1rem', background: '#f4f4f4', border: '1px solid #ddd', borderRadius: '20px', cursor: 'pointer', fontSize: '0.82rem', fontWeight: '700', color: '#555', fontFamily: 'Arial, sans-serif' }}
                  >
                    ← Back to Feed
                  </button>
                  <button
                    onClick={() => { setReadingArticle(false); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                    aria-label="Close article"
                    style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '36px', height: '36px', background: '#f4f4f4', border: '1px solid #ddd', borderRadius: '50%', cursor: 'pointer', fontSize: '1.1rem', color: '#555', lineHeight: 1 }}
                  >
                    ✕
                  </button>
                </div>

                {/* Sticky condensed header on scroll */}
                {articleScrolled && (
                  <div style={{ position: 'sticky', top: `${navTotalHeight}px`, zIndex: 10, backgroundColor: '#fff', borderBottom: '1px solid #e8e8e8', padding: '0.5rem 0', marginBottom: '1rem', display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '0.4rem' }}>
                    <span style={{ fontWeight: '700', fontSize: '0.9rem', color: '#1a1a1a' }}>{featured.title}</span>
                    <span style={{ color: '#ccc' }}>·</span>
                    <span style={{ fontSize: '0.8rem', color: '#555' }}>{featured.writer_name?.[0] || 'Author'}</span>
                    <span style={{ color: '#ccc' }}>·</span>
                    <span style={{ fontSize: '0.8rem', color: '#555' }}>{new Date(featured.date_created).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
                    <span style={{ color: '#ccc' }}>·</span>
                    <span style={{ fontSize: '0.8rem', color: '#555' }}>{company.company_name}</span>
                  </div>
                )}

                {/* Article header */}
                {featured.genre_tree?.main && (
                  <div style={{ fontSize: '0.75rem', color: '#888', marginBottom: '0.5rem', letterSpacing: '0.03em', fontFamily: 'Arial, sans-serif' }}>
                    {featured.genre_tree.main}{featured.genre_tree.sub ? ` / ${featured.genre_tree.sub.split(' › ').join(' / ')}` : ''}
                  </div>
                )}
                <h1 style={{ fontSize: '2.2rem', marginBottom: '0.75rem', lineHeight: 1.2, fontFamily: 'Georgia, serif', color: T.headText }}>
                  {featured.title}
                </h1>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '1rem', fontSize: '0.88rem', color: '#555', marginBottom: '1.5rem', fontFamily: 'Arial, sans-serif' }}>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1.25rem', alignItems: 'baseline' }}>
                    {featured.writer_name?.[0]
                      ? <span>Written By: <strong>{featured.writer_name[0]}</strong></span>
                      : <span><strong>{company?.company_name}</strong></span>
                    }
                    <span>Published: {new Date(featured.date_created).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
                  </div>
                  {featured.writer_name?.[0] && <span style={{ color: '#888', flexShrink: 0 }}>{company?.company_name}</span>}
                </div>
                {featured.headline_image && (
                  <img src={featured.headline_image} alt="" style={{ width: '100%', maxHeight: '420px', objectFit: 'contain', borderRadius: '6px', marginBottom: '1.5rem', display: 'block', background: '#f0f0f0' }} />
                )}

                {/* Full content — no maxHeight, page scrolls */}
                {featuredContent ? (
                  <>
                    <ArticleContentWrapper
                      html={featuredContent}
                      className="article-body"
                      style={{ color: '#333', lineHeight: '1.9', marginBottom: '2rem' }}
                    />
                    <style>{`.article-body img { max-width: 100%; border-radius: 6px; display: block; margin-bottom: 0.25rem; } .article-body figure { margin: 1rem 0; } .article-body figcaption { font-size: 0.82rem; color: #666; font-style: italic; padding: 0.2rem 0.5rem; border-left: 2px solid #ddd; }`}</style>
                  </>
                ) : (
                  <p style={{ color: '#888', fontSize: '0.9rem', marginBottom: '2rem' }}>Loading article...</p>
                )}

                {/* Kudos + share */}
                <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '0.75rem', paddingTop: '1rem', borderTop: '1px solid #eee', marginBottom: '0.5rem' }}>
                  <ShareButton title={featured.title} url={`/${slug}?a=${featured._id}`} />
                  <div style={{ display: 'flex', gap: '0.5rem', marginLeft: 'auto' }}>
                    <button onClick={() => toggleKudos('content')} title="Kudos for great content" style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', padding: '0.35rem 0.85rem', borderRadius: '20px', fontSize: '0.82rem', fontWeight: '600', cursor: artGiven.content ? 'default' : 'pointer', border: `1px solid ${artGiven.content ? '#28a745' : '#ccc'}`, backgroundColor: artGiven.content ? '#e8f8ee' : '#fff', color: artGiven.content ? '#28a745' : '#555' }}>
                      👍 {artKudos.content > 0 ? artKudos.content : ''} Content
                    </button>
                    {featured.writer_id && (
                      <button onClick={() => toggleKudos('author')} title="Kudos for the author" style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', padding: '0.35rem 0.85rem', borderRadius: '20px', fontSize: '0.82rem', fontWeight: '600', cursor: artGiven.author ? 'default' : 'pointer', border: `1px solid ${artGiven.author ? '#7b2ff7' : '#ccc'}`, backgroundColor: artGiven.author ? '#f3eeff' : '#fff', color: artGiven.author ? '#7b2ff7' : '#555' }}>
                        ⭐ {artKudos.author > 0 ? artKudos.author : ''} Author
                      </button>
                    )}
                  </div>
                </div>

                {/* Follow row */}
                {token && !isCompanyUser && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', paddingTop: '0.75rem', borderTop: '1px solid #eee', marginBottom: '2rem' }}>
                    <button onClick={handleFollowCompany} style={{ padding: '0.35rem 1rem', borderRadius: '20px', fontSize: '0.82rem', fontWeight: '600', cursor: 'pointer', border: `1px solid ${isFollowingCompany ? '#28a745' : '#0d3165'}`, backgroundColor: isFollowingCompany ? '#e8f8ee' : '#0d3165', color: isFollowingCompany ? '#28a745' : '#fff' }}>
                      {isFollowingCompany ? '✓ Following' : '+ Follow'} {company.company_name}
                    </button>
                    {featured.writer_id && (
                      <button onClick={() => handleFollowAuthor(featured.writer_id)} style={{ padding: '0.35rem 1rem', borderRadius: '20px', fontSize: '0.82rem', fontWeight: '600', cursor: 'pointer', border: `1px solid ${followingAuthors.includes(featured.writer_id) ? '#28a745' : '#555'}`, backgroundColor: followingAuthors.includes(featured.writer_id) ? '#e8f8ee' : '#fff', color: followingAuthors.includes(featured.writer_id) ? '#28a745' : '#555' }}>
                        {followingAuthors.includes(featured.writer_id) ? '✓ Following' : '+ Follow'} {featured.writer_name?.[0] || 'Author'}
                      </button>
                    )}
                  </div>
                )}
              </div>

            ) : (
              /* ── FEED MODE: preview cards ── */
              <>
                {/* Featured article preview */}
                {featured && (() => {
                  const canRead = isOwnCompanyPage || isArticlePurchased(featured._id);
                  return (
                    <div style={{ backgroundColor: '#fff', border: '2px solid #007bff', borderRadius: '8px', padding: '2rem', marginBottom: '2rem' }}>
                      {featured.genre_tree?.main && (
                        <div style={{ fontSize: '0.75rem', color: '#888', marginBottom: '0.5rem', letterSpacing: '0.03em' }}>
                          {featured.genre_tree.main}{featured.genre_tree.sub ? ` / ${featured.genre_tree.sub.split(' › ').join(' / ')}` : ''}
                        </div>
                      )}
                      <h2 style={{ fontSize: '1.8rem', marginBottom: '0.75rem', lineHeight: 1.2 }}>{featured.title}</h2>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '1rem', fontSize: '0.88rem', color: '#555', marginBottom: '1rem' }}>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1.25rem', alignItems: 'baseline' }}>
                          {featured.writer_name?.[0]
                            ? <span>Written By: <strong>{featured.writer_name[0]}</strong></span>
                            : <span><strong>{company?.company_name}</strong></span>
                          }
                          <span>Published: {new Date(featured.date_created).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
                        </div>
                        {featured.writer_name?.[0] && <span style={{ color: '#888', flexShrink: 0 }}>{company?.company_name}</span>}
                      </div>
                      {featured.headline_image && (
                        <img src={featured.headline_image} alt="" style={{ width: '100%', maxHeight: '280px', objectFit: 'contain', borderRadius: '6px', marginBottom: '1rem', display: 'block', background: '#f0f0f0' }} />
                      )}
                      <p style={{ color: '#555', lineHeight: '1.6', marginBottom: '1.5rem' }}>
                        {stripHtml(featured.content || '').substring(0, 300)}…
                      </p>

                      {/* Action row */}
                      <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '0.75rem', marginTop: '0.5rem' }}>
                        {isOwnCompanyPage ? (
                          <button className="btn-read" onClick={() => setReadingArticle(true)}>Read Article</button>
                        ) : canRead ? (
                          featured.external_url ? (
                            <a href={featured.external_url} target="_blank" rel="noopener noreferrer" style={{ display: 'inline-block', padding: '0.5rem 1.25rem', backgroundColor: '#0d3165', color: '#fff', borderRadius: '6px', textDecoration: 'none', fontWeight: '600', fontSize: '0.9rem' }}>
                              Read Article ↗
                            </a>
                          ) : (
                            <button className="btn-read" onClick={() => setReadingArticle(true)}>Read Article</button>
                          )
                        ) : token && !isCompanyUser ? (
                          (() => {
                            const state = getPurchaseState(featured);
                            if (state === 'normal') return <button className="btn btn-primary" onClick={() => openConfirm(featured)}>Purchase · {featured.token_cost} 🪙</button>;
                            if (state === 'promo')  return <button className="btn btn-warning"  onClick={() => openConfirm(featured)}>Purchase · {featured.token_cost} 🪙 ({featured.token_cost - user.tokens} gifted)</button>;
                            return (
                              <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', flexWrap: 'wrap' }}>
                                <span style={{ fontSize: '0.85rem', color: '#888' }}>{featured.token_cost} 🪙</span>
                                <button className="btn btn-secondary" onClick={() => navigate(`/tokens/purchase?next=${encodeURIComponent(`/${slug}?a=${featured._id}`)}`)}>Insufficient Tokens — Buy More</button>
                              </div>
                            );
                          })()
                        ) : !token ? (
                          <button className="btn btn-primary" onClick={() => navigate('/login')}>Login to Purchase · {featured.token_cost} 🪙</button>
                        ) : (
                          <div style={{ marginTop: '1rem', padding: '1rem', backgroundColor: '#f8f9fa', borderRadius: '6px', borderLeft: '4px solid #007bff', color: '#555', fontSize: '0.95rem' }}>
                            🔒 Purchase this article to read the full content.
                          </div>
                        )}

                        <ShareButton title={featured.title} url={`/${slug}?a=${featured._id}`} />

                        <div style={{ display: 'flex', gap: '0.5rem', marginLeft: 'auto' }}>
                          <button onClick={() => toggleKudos('content')} title="Kudos for great content" style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', padding: '0.35rem 0.85rem', borderRadius: '20px', fontSize: '0.82rem', fontWeight: '600', cursor: artGiven.content ? 'default' : 'pointer', border: `1px solid ${artGiven.content ? '#28a745' : '#ccc'}`, backgroundColor: artGiven.content ? '#e8f8ee' : '#fff', color: artGiven.content ? '#28a745' : '#555' }}>
                            👍 {artKudos.content > 0 ? artKudos.content : ''} Content
                          </button>
                          {featured.writer_id && (
                            <button onClick={() => toggleKudos('author')} title="Kudos for the author" style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', padding: '0.35rem 0.85rem', borderRadius: '20px', fontSize: '0.82rem', fontWeight: '600', cursor: artGiven.author ? 'default' : 'pointer', border: `1px solid ${artGiven.author ? '#7b2ff7' : '#ccc'}`, backgroundColor: artGiven.author ? '#f3eeff' : '#fff', color: artGiven.author ? '#7b2ff7' : '#555' }}>
                              ⭐ {artKudos.author > 0 ? artKudos.author : ''} Author
                            </button>
                          )}
                        </div>
                      </div>

                      {/* Follow row */}
                      {token && !isCompanyUser && (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginTop: '1rem', paddingTop: '0.75rem', borderTop: '1px solid #eee' }}>
                          <button onClick={handleFollowCompany} style={{ padding: '0.35rem 1rem', borderRadius: '20px', fontSize: '0.82rem', fontWeight: '600', cursor: 'pointer', border: `1px solid ${isFollowingCompany ? '#28a745' : '#0d3165'}`, backgroundColor: isFollowingCompany ? '#e8f8ee' : '#0d3165', color: isFollowingCompany ? '#28a745' : '#fff' }}>
                            {isFollowingCompany ? '✓ Following' : '+ Follow'} {company.company_name}
                          </button>
                          {featured.writer_id && (
                            <button onClick={() => handleFollowAuthor(featured.writer_id)} style={{ padding: '0.35rem 1rem', borderRadius: '20px', fontSize: '0.82rem', fontWeight: '600', cursor: 'pointer', border: `1px solid ${followingAuthors.includes(featured.writer_id) ? '#28a745' : '#555'}`, backgroundColor: followingAuthors.includes(featured.writer_id) ? '#e8f8ee' : '#fff', color: followingAuthors.includes(featured.writer_id) ? '#28a745' : '#555' }}>
                              {followingAuthors.includes(featured.writer_id) ? '✓ Following' : '+ Follow'} {featured.writer_name?.[0] || 'Author'}
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })()}

                {/* ── More articles ── */}
                {filteredMore.length > 0 && (
                  <div>
                    <h3 style={{ marginTop: '2rem', marginBottom: '1rem' }}>
                      {activeGenre ? `${activeGenre}${activeSub ? ` › ${activeSub}` : ''}` : `More from ${company.company_name}`}
                    </h3>
                    <div className="article-feed">
                      {filteredMore.map(article => (
                        <div key={article._id} className="article-card">
                          <div className="article-header" style={{ flexDirection: 'row', alignItems: 'flex-start', gap: '1rem' }}>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              {article.genre_tree?.main && (
                                <div style={{ fontSize: '0.72rem', color: '#888', marginBottom: '0.25rem', letterSpacing: '0.03em' }}>
                                  {article.genre_tree.main}{article.genre_tree.sub ? ` / ${article.genre_tree.sub.split(' › ').join(' / ')}` : ''}
                                </div>
                              )}
                              <h3 className="article-title" style={{ margin: 0 }}>{article.title}</h3>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '1rem', fontSize: '0.82rem', color: '#555', marginTop: '0.3rem' }}>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem', alignItems: 'baseline' }}>
                                  {article.writer_name?.[0]
                                    ? <span>Written By: <strong>{article.writer_name[0]}</strong></span>
                                    : <span><strong>{company?.company_name}</strong></span>
                                  }
                                  <span>Published: {new Date(article.date_created).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
                                  {article.kudos > 0 && <span style={{ color: '#7b2ff7' }}>👍 {article.kudos}</span>}
                                </div>
                                {article.writer_name?.[0] && <span style={{ color: '#888', flexShrink: 0 }}>{company?.company_name}</span>}
                              </div>
                            </div>
                        {article.headline_image && (
                          <img src={article.headline_image} alt="" style={{ width: '110px', height: '75px', objectFit: 'cover', borderRadius: '6px', flexShrink: 0, border: '1px solid #e8e8e8' }} />
                        )}
                      </div>
                      <p className="article-blurb" style={{ marginTop: '0.5rem' }}>{(article.excerpt || stripHtml(article.content)).substring(0, 200)}...</p>
                      <div className="article-footer">
                        <button
                          className="btn-read"
                          onClick={() => {
                            if (article.external_url && (isOwnCompanyPage || isArticlePurchased(article._id))) {
                              window.open(article.external_url, '_blank', 'noopener,noreferrer');
                            } else if (!article.external_url && (isOwnCompanyPage || isArticlePurchased(article._id))) {
                              setSelectedArticle(article); setActiveGenre(null); setActiveSub(null); setReadingArticle(true);
                            } else {
                              setSelectedArticle(article); setActiveGenre(null); setActiveSub(null);
                            }
                          }}
                        >
                          {article.external_url ? 'View Article ↗' : 'View Article'}
                        </button>
                        {!isOwnCompanyPage && <span style={{ fontSize: '0.9rem', color: '#666' }}>{article.token_cost} 🪙</span>}
                        <ShareButton title={article.title} url={`/${slug}?a=${article._id}`} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
              </>
            )}
          </div>

          {/* Right sidebar — desktop only */}
          {!isMobile && <div style={{ width: '300px', flexShrink: 0, borderLeft: `1px solid ${T.border}`, position: 'sticky', top: `${navTotalHeight}px`, height: `calc(100vh - ${navTotalHeight}px)`, overflowY: 'auto', padding: '20px 16px', transition: 'top 0.3s ease, height 0.3s ease' }}>

            {/* More articles — LP5 SideCard style */}
            <div style={{ background: T.accent, padding: '8px 12px', marginBottom: '14px' }}>
              <span style={{ fontSize: '12px', fontWeight: '900', color: T.white, textTransform: 'uppercase', letterSpacing: '0.08em', fontFamily: 'Arial, sans-serif' }}>
                More Articles
              </span>
            </div>
            {articles.length === 0 ? (
              <div style={{ fontSize: '13px', color: T.meta, fontFamily: 'Arial, sans-serif' }}>No articles yet.</div>
            ) : (
              articles.slice(0, 10).map(a => (
                <SideCard
                  key={a._id}
                  article={a}
                  onClick={() => { setSelectedArticle(a); setActiveGenre(null); setActiveSub(null); }}
                />
              ))
            )}

          </div>}
        </div>
      </div>

      {/* ── Purchase confirm modal (unchanged) ── */}
      {confirmModal && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000 }}>
          <div style={{ backgroundColor: '#fff', borderRadius: '10px', padding: '2rem', maxWidth: '420px', width: '90%', boxShadow: '0 4px 24px rgba(0,0,0,0.2)' }}>
            <h4 style={{ marginBottom: '0.75rem' }}>Confirm Purchase</h4>
            <p style={{ marginBottom: '0.5rem' }}><strong>{confirmModal.article.title}</strong></p>
            <p style={{ marginBottom: '1.25rem', color: '#555', fontSize: '0.95rem' }}>
              {confirmModal.state === 'promo'
                ? `This article costs ${confirmModal.article.token_cost} credits but you only have ${user.tokens}. We'll gift you ${confirmModal.article.token_cost - user.tokens} Promotional Credit${confirmModal.article.token_cost - user.tokens !== 1 ? 's' : ''} to cover the difference. Your balance will be 0 after this purchase.`
                : `This will cost ${confirmModal.article.token_cost} credit${confirmModal.article.token_cost !== 1 ? 's' : ''}. Your balance after purchase: ${user.tokens - confirmModal.article.token_cost} credit${user.tokens - confirmModal.article.token_cost !== 1 ? 's' : ''}.`
              }
            </p>
            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
              <button className="btn btn-secondary" onClick={() => setConfirmModal(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={confirmPurchase}>Confirm</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default CompanyPage;
